import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import moment from "moment";
import EmptyState from "./EmptyState";
import Pagination from "./Pagination";
import Badge from "./Badge";
import DateRangeSelect from "./DateRangeSelect";
import AgencySelect from "./AgencySelect";
import ExportButton from "./ExportButton";
import {
  MobileExportButtonWrapper,
  FiltersRow,
  FiltersGroup,
  ExportButtonWrapper
} from "containers/impact-report/index.styled";
import { ROWS_PER_PAGE } from "containers/impact-report/constants";
import { formatLocation } from "utils/formatUtils";
import { filterByDateRange, filterByAgency } from "utils/filterUtils";
import {
  SectionTitle,
  TableWrapper,
  Table,
  TableHead,
  StyledTableHeader,
  StyledTableCell,
  StyledTableLink,
  SortableHeaderContent,
  SortIcon,
  SortArrow
} from "./CommunityRequestsTable.styled";

const CommunityRequestsTable = ({
  communityRequests,
  exportUrl,
  isLoadingExport,
  hasExportError
}) => {
  const { t } = useTranslation();
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");

  const handleClick = useCallback((resourceId, resourceName) => {
    if (window.RWA) {
      try {
        window.RWA.recordEvent({
          resourceId,
          type: "click",
          resourceName
        });
      } catch (error) {
        console.error("Failed to record click:", error);
      }
    }
  }, []);

  const filteredRequests = useMemo(() => {
    const dateFiltered = filterByDateRange(
      communityRequests,
      selectedDateRange
    );
    const agencyFiltered = filterByAgency(dateFiltered, selectedAgency);

    return [...agencyFiltered].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [communityRequests, selectedDateRange, selectedAgency, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDateRange, selectedAgency, sortOrder]);

  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  if (!communityRequests.length) return <EmptyState />;

  return (
    <>
      <SectionTitle>
        {t("impactReport.communityRequests.sectionTitle")}
      </SectionTitle>
      <FiltersRow>
        <FiltersGroup>
          <DateRangeSelect
            communityRequests={communityRequests}
            selectedRange={selectedDateRange}
            onRangeChange={setSelectedDateRange}
          />
          <AgencySelect
            communityRequests={communityRequests}
            selectedAgency={selectedAgency}
            onAgencyChange={setSelectedAgency}
          />
        </FiltersGroup>
        <ExportButtonWrapper>
          <ExportButton
            exportUrl={exportUrl}
            isLoadingExport={isLoadingExport}
            hasExportError={hasExportError}
          />
        </ExportButtonWrapper>
      </FiltersRow>
      <TableWrapper>
        <Table>
          <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <TableHead>
            <tr>
              <StyledTableHeader
                sortable
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
              >
                <SortableHeaderContent>
                  <span>{t("impactReport.table.date")}</span>
                  <SortIcon>
                    <SortArrow direction={"up"} active={sortOrder === "asc"} />
                    <SortArrow
                      direction={"down"}
                      active={sortOrder === "desc"}
                    />
                  </SortIcon>
                </SortableHeaderContent>
              </StyledTableHeader>
              <StyledTableHeader>
                {t("impactReport.table.agencyName")}
              </StyledTableHeader>
              <StyledTableHeader>
                {t("impactReport.table.communityRequest")}
              </StyledTableHeader>
              <StyledTableHeader>
                {t("impactReport.table.location")}
              </StyledTableHeader>
              <StyledTableHeader>
                {t("impactReport.table.evidencePlatform")}
              </StyledTableHeader>
            </tr>
          </TableHead>
          <tbody>
            {paginatedRequests.map(request => (
              <tr key={request.solicitation_id}>
                <StyledTableCell>
                  {moment(request.created_at).format("MM/DD/YY")}
                </StyledTableCell>
                <StyledTableCell>
                  <StyledTableLink
                    href={`/agencies/${request.agency_uuid}`}
                    onClick={() =>
                      handleClick(
                        "impact-report-table-agency-name-link",
                        request.agency_name
                      )
                    }
                  >
                    {request.agency_name}
                  </StyledTableLink>
                </StyledTableCell>
                <StyledTableCell truncate>
                  <StyledTableLink
                    href={request.post_url}
                    onClick={() =>
                      handleClick(
                        "impact-report-table-community-request-link",
                        request.solicitation_title
                      )
                    }
                  >
                    {request.solicitation_title}
                  </StyledTableLink>
                </StyledTableCell>
                <StyledTableCell>
                  {formatLocation(request.city, request.state)}
                </StyledTableCell>
                <StyledTableCell>
                  <Badge value={request.evidence_platform}>
                    {request.evidence_platform}
                  </Badge>
                </StyledTableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <Pagination
        currentPage={currentPage}
        totalItems={filteredRequests.length}
        itemsPerPage={ROWS_PER_PAGE}
        onPageChange={setCurrentPage}
      />
      <MobileExportButtonWrapper>
        <ExportButton
          exportUrl={exportUrl}
          isLoadingExport={isLoadingExport}
          hasExportError={hasExportError}
        />
      </MobileExportButtonWrapper>
    </>
  );
};

CommunityRequestsTable.propTypes = {
  communityRequests: PropTypes.array.isRequired,
  exportUrl: PropTypes.string,
  isLoadingExport: PropTypes.bool.isRequired,
  hasExportError: PropTypes.bool.isRequired
};

export default CommunityRequestsTable;
